<template>
  <Card class="bg-grey-background">
    <div class="container">
      <Form :disabled="loading" @submit.prevent="validateAndGoToPayment">
        <div class="grid">
          <Card class="grid-item -col-span-12 -col-span-lg-6 -col-span-xl-4" :class="[{ 'pa-5': isMobile }, { 'pa-10': isNotMobile }]">
            <header class="d-flex">
              <VIcon icon="far fa-location-dot" size="24px" class="mr-3" />
              <h6 class="paragraph-large font-weight-regular">{{ strapi.Delivery.DeliveryAddressTitle }}</h6>
              <div class="ml-auto">
                <AddDeliveryAddress :triggerLabel="strapi.Delivery.AddDeliveryAdressLabel" :triggerIcon="strapi.Delivery.AddDeliveryAdressIcon" @onUpdateDialog="dialog = $event" />
              </div>
            </header>
            <VDivider :class="[{ 'mt-2 mb-5': isMobile }, { 'mt-4 mb-10': isNotMobile }]" />
            <div>
              <RadioGroup v-model="selectedCustomerDeliveryAddress">
                <div v-for="(customerDeliveryAddress, index) in customer.customerDeliveryAddresses" :key="customerDeliveryAddress.id" class="w-100">
                  <div class="w-100 d-flex justify-space-between">
                    <VRadio class="align-baseline flex-shrink-1" :value="customerDeliveryAddress" name="checkout.selectedCustomerDeliveryAddress">
                      <template #label>
                        <div class="d-flex flex-column">
                          <p class="-no-margin">{{ customerDeliveryAddress.address }}</p>
                          <p v-if="customerDeliveryAddress.address2" class="-no-margin">{{ customerDeliveryAddress.address2 }}</p>
                          <p class="-no-margin">
                            {{ customerDeliveryAddress.city }},
                            <span v-if="customerDeliveryAddress.state">{{ customerDeliveryAddress.state.code }}</span>
                            {{ customerDeliveryAddress.zip }}
                          </p>
                          <p class="-no-margin">{{ customerDeliveryAddress.country.name }}</p>
                          <p v-if="customerDeliveryAddress.vat" class="-no-margin">{{ customerDeliveryAddress.vat.vat_number }}</p>
                        </div>
                      </template>
                    </VRadio>
                    <EditDialog :strapi="strapi.EditAddress" :customerDeliveryAddress="customerDeliveryAddress" :onSave="onNavigatedBack" @onUpdateDialog="dialog = $event" />
                  </div>

                  <VDivider v-if="customer.customerDeliveryAddresses[index + 1]" class="my-6" />
                </div>
              </RadioGroup>
            </div>
          </Card>
          <div class="grid-item -col-span-12 -col-span-lg-6 -col-span-xl-8">
            <Card :class="[{ 'pa-5': isMobile }, { 'pa-10': isNotMobile }]">
              <template v-if="deliveryCountryBanned">
                <header class="d-flex">
                  <VIcon icon="far fa-exclamation-triangle" size="24px" class="mr-3" />
                  <h6 class="paragraph-large font-weight-regular">Delivery Address Not Available</h6>
                </header>
                <VDivider :class="[{ 'mt-2 mb-5': isMobile }, { 'mt-4 mb-10': isNotMobile }]" />
                <div>
                  <p>
                    Due to new import rules, delivery addresses in Romania are not accepted at the moment.
                    <br />
                    We are working to resolve this issue.
                    <br />
                    For further information, please contact Techship Sales.
                  </p>
                  <a aria-label="Techship Sales Email Address" class="paragraph underline-primary d-block" href="mailto:sales@techship.com">sales@techship.com</a>
                </div>
              </template>
              <template v-else>
                <header class="d-flex">
                  <VIcon icon="far fa-truck" size="24px" class="mr-3" />
                  <h6 class="paragraph-large font-weight-regular">{{ strapi.Delivery.FreightOptionTitle }}</h6>
                </header>
                <VDivider :class="[{ 'mt-2 mb-5': isMobile }, { 'mt-4 mb-10': isNotMobile }]" />
                <div class="d-flex flex-column">
                  <template v-if="PartialDeliveryIsAvailable">
                    <VRow>
                      <VCol cols="12" xl="6" class="pl-xl-5">
                        <p class="paragraph -no-margin">{{ strapi.Delivery.PartialDelivery.ProductsLabel }}</p>
                        <a v-for="cartItem in cartItemsNotAvailableInRequestedQuantity" :key="cartItem" :href="cartItem.articleVariant.route.publicShow" class="d-block font-weight-bold" aria-label="go to product page">{{ cartItem.article.name }}</a>
                      </VCol>
                      <VCol cols="12" xl="6" class="pr-xl-5">
                        <label for="checkoutPartialDelivery">{{ strapi.Delivery.PartialDelivery.Label }}</label>
                        <RadioGroup id="checkoutIsPartialDelivery" v-model="isPartialDelivery" name="checkout.isPartialDelivery" :disabled="loading" :hint="strapi.Delivery.PartialDelivery.Hint" persistentHint>
                          <Radio :value="true" :label="strapi.Delivery.PartialDelivery.PartialDeliveryLabel" />
                          <Radio :value="false" :label="strapi.Delivery.PartialDelivery.DeliverInFullLabel" />
                        </RadioGroup>
                      </VCol>
                    </VRow>
                    <VDivider class="my-6" />
                  </template>
                  <template v-if="partialOrFullDeliveryHasBeenSelectedOrNotAvailable">
                    <VRow>
                      <VCol cols="12" xl="6" class="pr-xl-5">
                        <label for="checkoutFreightMethod">{{ strapi.Delivery.FreightMethodLabel }}</label>
                        <Select id="checkoutFreightMethod" v-model="selectedFreightMethod" :items="customer.shop.freightMethods" :disabled="loading" :itemTitle="freightMethod => `${freightMethod.customer_description} (${freightMethod.code})`" returnObject name="checkout.delivery.freightMethod" />
                      </VCol>
                      <!--TODO: Strapify? -->
                      <VCol cols="12" xl="6" class="pl-xl-5">
                        <p class="paragraph -no-margin">{{ selectedFreightMethod?.customer_extended_description }}</p>
                      </VCol>
                    </VRow>
                    <VDivider class="my-6" />
                    <VRow>
                      <VCol cols="12" xl="6" class="pr-xl-5">
                        <label for="checkoutFreightCompany">{{ strapi.Delivery.FreightCompanyLabel }}</label>
                        <Select id="checkoutFreightCompany" v-model="selectedFreightCompany" :disabled="$isBlank(selectedFreightMethod) || loading" :items="freightCompanies" itemTitle="name" returnObject name="checkout.delivery.freightCompany" />
                      </VCol>
                      <!--TODO: Strapify? -->
                      <VCol cols="12" xl="6" class="pl-xl-5">
                        <p class="paragraph -no-margin">{{ freightCompanyServiceCodeDescription }}</p>
                      </VCol>
                    </VRow>
                    <VDivider class="my-6" />
                    <VRow>
                      <VCol cols="12" xl="6" class="pr-xl-5">
                        <label for="checkoutFreightCompanyServiceCodes">{{ strapi.Delivery.FreightServiceLabel }}</label>
                        <Select id="checkoutFreightCompanyServiceCodes" v-model="selectedFreightServiceCode" :disabled="$isBlank(selectedFreightCompany) || loading" :items="freightCompanyServiceCodes" itemTitle="code" returnObject name="checkout.delivery.freightCompanyServiceCode" />
                      </VCol>
                      <VCol v-if="isFCA" cols="12" xl="6" class="pl-xl-5">
                        <label for="checkoutFreightCompanyAccountNumber">{{ strapi.Delivery.AccountNumberLabel }}</label>
                        <TextField id="checkoutFreightCompanyAccountNumber" v-model="freightCompanyAccountNumber" name="checkout.delivery.freightCompanyAccountNumber" :placeholder="strapi.Delivery.AccountNumberPlaceholder" />
                      </VCol>
                    </VRow>
                    <VDivider class="my-6" />
                    <VRow>
                      <VCol cols="12" xl="6">
                        <label for="checkoutPONumber">{{ strapi.Delivery.PurchaseOrderNumberLabel }}</label>
                        <TextField id="checkoutPONumber" v-model="poNumber" name="checkout.delivery.poNumber" :placeholder="strapi.Delivery.PurchaseOrderNumberPlaceholder" :hint="strapi.Delivery.PurchaseOrderNumberHint" />
                      </VCol>
                    </VRow>
                    <VDivider class="my-6" />
                  </template>
                  <VRow>
                    <VCol cols="12" class="d-flex justify-space-between">
                      <div>
                        <template v-if="estimatedDeliveryCostWithCurrency">
                          <span class="d-block paragraph -no-margin">{{ strapi.Delivery.DeliveryCost }}</span>
                          <span>{{ $moneyFormat(estimatedDeliveryCostWithCurrency.cost, estimatedDeliveryCostWithCurrency.currency.code) }}</span>
                        </template>
                      </div>
                      <Button iconButton class="bg-accent" color="text-white" :disabled="allFieldsNotSelected || loading" :loading="loading" type="submit">
                        {{ strapi.Delivery.ContinueButton.Label }}
                        <VIcon icon="fas fa-chevron-right" />
                      </Button>
                    </VCol>
                  </VRow>
                </div>
              </template>
            </Card>
          </div>
        </div>
      </Form>
    </div>
  </Card>
</template>

<script>
import dialog from '@/plugins/globals/dialog';
import AddDeliveryAddress from '@Components/AddDeliveryAddress.vue';
import EditDialog from '@Views/checkout/components/EditDialog.vue';
export default {
  name: 'Delivery',
  components: { AddDeliveryAddress, EditDialog },
  props: {
    customer: {
      type: Object,
      required: true
    },
    totalQuantityForAllowedCheckoutItems: {
      type: Number,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    strapi: {
      type: Object,
      default: () => null
    }
  },
  emits: ['update:loading', 'nextStep'],
  data() {
    return {
      cartItemsNotAvailableInRequestedQuantity: [],
      freightCompanies: [],
      freightCompanyServiceCodes: [],
      selectedCustomerDeliveryAddress: this.$first(this.customer.customerDeliveryAddresses),
      isPartialDelivery: null,
      poNumber: null,
      selectedFreightMethod: null,
      // selectedFreightMethod: this.$first(this.customer.shop.freightMethods),
      selectedFreightCompany: null,
      selectedFreightServiceCode: null,
      freightCompanyAccountNumber: null,
      estimatedDeliveryCostWithCurrency: null,
      dialog: false,
      deliveryCountryBanned: false
      // dialogId: this.$uuid()
    };
  },
  computed: {
    allFieldsNotSelected() {
      if (this.PartialDeliveryIsAvailable && this.$isBlank(this.isPartialDelivery)) {
        return true;
      }

      if (this.isFCA && this.$isBlank(this.freightCompanyAccountNumber)) {
        return true;
      }
      return this.$isBlank(this.selectedCustomerDeliveryAddress) || this.$isBlank(this.selectedFreightMethod) || this.$isBlank(this.selectedFreightCompany) || this.$isBlank(this.selectedFreightServiceCode);
    },
    PartialDeliveryIsAvailable() {
      return this.$isNotEmpty(this.cartItemsNotAvailableInRequestedQuantity) && this.totalQuantityForAllowedCheckoutItems > 1;
    },
    partialOrFullDeliveryHasBeenSelectedOrNotAvailable() {
      return this.isPartialDelivery !== null || !this.PartialDeliveryIsAvailable;
    },
    isFCA() {
      return this.selectedFreightMethod?.code === 'FCA';
    },
    freightCompanyServiceCodeDescription() {
      if (!this.selectedFreightServiceCode) return;
      return this.customer.shop.company.code === 'AB' ? this.selectedFreightServiceCode.description : this.selectedFreightServiceCode.description_inc;
    }
  },
  watch: {
    async isPartialDelivery() {
      if (this.partialOrFullDeliveryHasBeenSelectedOrNotAvailable) {
        await this.getEstimatedDeliveryCost();
      }
    },
    async selectedCustomerDeliveryAddress() {
      await this.checkDeliveryAddressBanned();
      await this.getFreightCompanies();
    },
    selectedFreightMethod: {
      async handler(val) {
        await this.getFreightCompanies();
      }
      // immediate: true
    },
    async selectedFreightServiceCode() {
      this.freightCompanyAccountNumber = null;
      if (this.partialOrFullDeliveryHasBeenSelectedOrNotAvailable) {
        await this.getEstimatedDeliveryCost();
      }
    },
    freightCompanies(value) {
      this.selectedFreightCompany = this.$first(value);
    },
    async selectedFreightCompany() {
      await this.getFreightCompanyServiceCodes();
      if (this.isFCA) {
        await this.getFreightCompanyAccountNumber();
      }
    },
    freightCompanyServiceCodes() {
      this.selectedFreightServiceCode = this.$first(this.freightCompanyServiceCodes);
    }
  },
  async created() {
    // this.$mitt.on(`dialog:cancel:${this.dialogId}`, () => {
    //   this.$redirect('/');
    // });
    this.selectedFreightMethod = this.$first(this.customer.shop.freightMethods);
    await this.getCartItemsNotAvailableInRequestedQuantity();
    // if (this.$isNotEmpty(this.cartItemsNotAvailableInRequestedQuantity)) {
    //   this.$dialog.warning({ title: 'Warning', body: 'This is a warning message' }, { id: this.dialogId, persistent: true });
    // }
  },
  async mounted() {
    await this.checkDeliveryAddressBanned();

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'checkoutDeliveryStepLoaded'
    });
  },
  methods: {
    async getCartItemsNotAvailableInRequestedQuantity() {
      this.$emit('update:loading', true);
      const response = await this.get('/checkout/get-cart-items-not-available-in-requested-quantity/');
      if (!response) {
        this.$emit('update:loading', false);
        return;
      }
      this.$emit('update:loading', false);
      this.cartItemsNotAvailableInRequestedQuantity = response;
    },
    async checkDeliveryAddressBanned() {
      this.$emit('update:loading', true);
      const response = await this.get('/checkout/check-delivery-address-banned/', { customerDeliveryAddressId: this.selectedCustomerDeliveryAddress.id });

      if (!response) {
        this.$emit('update:loading', false);
        return;
      }

      if (response.error) {
        this.$snackbar.error(response.error);
        this.$emit('update:loading', false);
        return;
      }

      this.deliveryCountryBanned = response.banned ?? false;

      this.$emit('update:loading', false);
    },
    async getFreightCompanies() {
      this.$emit('update:loading', true);
      const response = await this.get('/checkout/get-freight-companies/', { customerDeliveryAddressId: this.selectedCustomerDeliveryAddress.id, freightMethodId: this.selectedFreightMethod.id });
      if (!response) {
        this.$emit('update:loading', false);
        return;
      }
      this.$emit('update:loading', false);
      this.freightCompanies = response;
    },
    async getFreightCompanyServiceCodes() {
      this.$emit('update:loading', true);
      const response = await this.get('/checkout/get-freight-company-service-codes/', { freightCompanyId: this.selectedFreightCompany.id, customerDeliveryAddressId: this.selectedCustomerDeliveryAddress.id, freightMethodId: this.selectedFreightMethod.id });
      if (!response) {
        this.$emit('update:loading', false);
        return;
      }
      this.$emit('update:loading', false);
      this.freightCompanyServiceCodes = response;
    },
    async getFreightCompanyAccountNumber() {
      this.$emit('update:loading', true);
      const response = await this.get('/checkout/get-freight-company-account-number/', { freightCompanyId: this.selectedFreightCompany.id, customerDeliveryAddressId: this.selectedCustomerDeliveryAddress.id, freightCompanyServiceCodeId: this.selectedFreightServiceCode.id });
      if (!response) {
        this.$emit('update:loading', false);
        return;
      }
      this.$emit('update:loading', false);
      this.freightCompanyAccountNumber = response;
    },
    async getEstimatedDeliveryCost() {
      this.estimatedDeliveryCostWithCurrency = null;
      this.$emit('update:loading', true);
      const response = await this.get('/checkout/get-estimated-delivery-cost/', { freightCompanyServiceCodeId: this.selectedFreightServiceCode.id, freightCompanyAccountNumber: this.freightCompanyAccountNumber, freightCompanyId: this.selectedFreightCompany.id, isPartialDelivery: this.isPartialDelivery });
      if (!response) {
        this.$emit('update:loading', false);
        return;
      }
      this.$emit('update:loading', false);
      this.estimatedDeliveryCostWithCurrency = response;
    },
    async validateAndGoToPayment() {
      await this.getEstimatedDeliveryCost();
      if (this.allFieldsNotSelected || this.loading) return;
      this.$emit('update:loading', true);
      const response = await this.post('/checkout/validate/delivery/', { poNumber: this.poNumber });
      if (!response) {
        this.$emit('update:loading', false);
        return;
      }
      this.$emit('update:loading', false);
      this.$emit('nextStep');
    },
    async onNavigatedBack() {
      await this.getCartItemsNotAvailableInRequestedQuantity();
      if (this.PartialDeliveryIsAvailable) {
        this.estimatedDeliveryCostWithCurrency = null;
        if (this.isPartialDelivery != null) {
          await this.getEstimatedDeliveryCost();
        }
      } else {
        this.isPartialDelivery = null;
        await this.getEstimatedDeliveryCost();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
:deep(.v-label) {
  opacity: 1 !important;
}
</style>
